import { useCallback } from 'react'

import { Header } from '@circlefin/components'
import { usePermission } from '@circlefin/permissions/react'
import { CUSTODY_POLICY_SECTION } from '@services/permissions'
import { TypeGuards } from '@services/type-guards'
import { TransactionCard } from '@shared/components/common'
import {
  useWalletApprovalTransactionDetailByJobIdQuery,
  usePaymentDetailsQuery,
  TransactionType,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { Fees, FundingSource } from '../../components'
import { WalletApprovalTransactionDetailApprovalSummary } from '../WalletApprovalTransactionDetailApprovalSummary/WalletApprovalTransactionDetailApprovalSummary'

export interface WireWithdrawalDetailProps {
  /**
   * Wire Receive Transaction ID.
   */
  id: string
}

export const WireWithdrawalDetail: React.FC<WireWithdrawalDetailProps> = ({
  id,
}) => {
  const { t } = useTranslation('payments')
  const [, { isAuthorized }] = usePermission(CUSTODY_POLICY_SECTION)

  const wireWithdrawal = usePaymentDetailsQuery({
    variables: {
      id,
    },
  })

  const walletApproval = useWalletApprovalTransactionDetailByJobIdQuery({
    variables: {
      jobId: wireWithdrawal.data?.paymentDetails.payment.id ?? '',
    },
    skip: !isAuthorized || !wireWithdrawal.data?.paymentDetails.payment.id,
  })

  const onRetry = useCallback(() => {
    void wireWithdrawal.refetch()
    void walletApproval.refetch()
  }, [wireWithdrawal, walletApproval])

  const paymentDetails =
    TypeGuards.Payments.isPaymentDetails<TransactionType.wire_withdrawal>(
      wireWithdrawal.data?.paymentDetails,
      TransactionType.wire_withdrawal,
    )
      ? wireWithdrawal.data.paymentDetails
      : undefined

  return (
    <>
      <Header variant="secondary">
        <Header.Head title={t`payments:transactionDetail.title`} />
        <Header.Description>
          {t('payments:transactionDetail.transactionId', {
            id: wireWithdrawal.data?.paymentDetails.payment.id ?? '',
          })}
        </Header.Description>
      </Header>

      <TransactionCard
        error={wireWithdrawal.error}
        loading={wireWithdrawal.loading}
        onRetry={onRetry}
        transaction={paymentDetails?.payment}
      >
        {walletApproval.data?.walletApprovalTransactionDetailByJobId && (
          <WalletApprovalTransactionDetailApprovalSummary
            {...walletApproval.data.walletApprovalTransactionDetailByJobId}
          />
        )}

        {paymentDetails?.destination != null && (
          <FundingSource
            description={paymentDetails.destination.description}
            label={t('transactionDetail.destination')}
            name={paymentDetails.destination.nickname}
          />
        )}

        <Fees fees={paymentDetails?.fees} />
      </TransactionCard>
    </>
  )
}
